import React, {
    useState,
    useImperativeHandle,
    forwardRef,
    useRef,
} from 'react';
import PropTypes from 'prop-types';
import DotLoader from '@/common/dot-loader';
import { useTranslation } from 'react-i18next';

import {
    TermsAndConditionsInput,
    SubscriberInput,
} from '@/desktop/customer/inputs';

import { getE164PhoneNumberFromCountryAndNationalPhoneNumber } from '@/common/helpers/phone_number';
import { PhoneNumberInputWithValidation as DesktopPhoneNumberInputWithValidation } from '@/desktop/common/inputs/phone-number';
import { PhoneNumberInputWithValidation as MobilePhoneNumberInputWithValidation } from '@/mobile/common/inputs/phone-number';

const CustomerPhoneNumberForm = forwardRef(({ onComplete }, ref) => {
    const [t] = useTranslation();
    const [isLoading, setIsLoading] = useState(false);

    const [newPhoneNumber, setNewPhoneNumber] = useState('');
    const [phoneNumberCountry, setPhoneNumberCountry] = useState(
        Mobi2Go.headoffice.country_iso3166_alpha2
    );
    const [phoneNumberErrorMessage, setPhoneNumberErrorMessage] = useState('');
    const phoneNumberErrorRef = useRef(null);

    const [termsAndConditions, setTermsAndConditions] = useState(false);
    const [subscriber, setSubscriber] = useState(false);

    useImperativeHandle(ref, () => ({}), []);

    const saveToServer = () => {
        setIsLoading(true);
        let phoneNumberValidated = newPhoneNumber;

        try {
            phoneNumberValidated = getE164PhoneNumberFromCountryAndNationalPhoneNumber(
                phoneNumberCountry,
                newPhoneNumber
            );
        } catch (e) {
            handleUpdateError('Invalid phone_number number');
            setIsLoading(false);
            return;
        }

        try {
            Mobi2Go.Customer.update(
                {
                    phone_number: phoneNumberValidated,
                    is_subscriber: subscriber,
                },
                (status, error) => {
                    if (status === 'error' && error) {
                        handleUpdateError(error);
                    } else {
                        onComplete();
                    }
                    setIsLoading(false);
                }
            );
        } catch (error) {
            setIsLoading(false);
        }
    };

    const animatePhoneNumberError = () => {
        if (this.phone_number_error_ref) {
            Mobi2Go.UI.helpers.setAnimation(
                phoneNumberErrorRef.current,
                'rock',
                true
            );
        }
    };

    const handleUpdateError = error => {
        if (error === 'Invalid phone_number number') {
            setPhoneNumberErrorMessage('Invalid phone number', () => {
                if (
                    phoneNumberErrorMessage !== '' &&
                    phoneNumberErrorRef.current
                ) {
                    animatePhoneNumberError();
                }
            });
        } else {
            setPhoneNumberErrorMessage(JSON.stringify(error));
        }
    };

    return (
        <div className="m2g-customer-phone-number">
            <>
                <p className="m2g-customer-phone-number-explanation">
                    {t('customer:register_enter_phone_body')}
                </p>
                {Mobi2Go.app.getPlatform() === 'web' ? (
                    <>
                        <DesktopPhoneNumberInputWithValidation
                            value={newPhoneNumber}
                            country={phoneNumberCountry}
                            error={phoneNumberErrorMessage}
                            placeholder={t(
                                'customer.details:phone_number_placeholder'
                            )}
                            containerClassName="m2g-customer-phone-number"
                            onChange={phoneNumber =>
                                setNewPhoneNumber(phoneNumber)
                            }
                            onCountryChange={country =>
                                setPhoneNumberCountry(country)
                            }
                            onBlur={() => {}}
                            // errorRef={phoneNumberErrorRef}
                        />
                        <TermsAndConditionsInput
                            value={termsAndConditions}
                            onChange={value => setTermsAndConditions(value)}
                            onClick={() =>
                                Mobi2Go.UI.trigger(
                                    'showPolicy',
                                    'termsandconditions'
                                )
                            }
                        />
                        <SubscriberInput
                            value={subscriber}
                            onChange={value => setSubscriber(value)}
                        />
                    </>
                ) : (
                    <>
                        <MobilePhoneNumberInputWithValidation
                            value={newPhoneNumber}
                            country={phoneNumberCountry}
                            error={phoneNumberErrorMessage}
                            placeholder={t(
                                'customer.details:phone_number_placeholder'
                            )}
                            containerClassName="m2g-customer-phone-number"
                            onChange={phoneNumber =>
                                setNewPhoneNumber(phoneNumber)
                            }
                            onCountryChange={country =>
                                setPhoneNumberCountry(country)
                            }
                            onBlur={() => {}}
                            // errorRef={phoneNumberErrorRef}
                        />
                        <div
                            role="checkbox"
                            tabIndex={0}
                            data-terms-and-conditions
                            data-checked={termsAndConditions}
                            aria-checked={termsAndConditions}
                            className="m2g-list-cell m2g-customer-terms-and-conditions-message m2g-touchable"
                            onClick={() =>
                                setTermsAndConditions(!termsAndConditions)
                            }
                            onKeyUp={e => {
                                if (e.key !== ' ') {
                                    return;
                                }
                                setTermsAndConditions(!termsAndConditions);
                            }}
                        >
                            <div className="m2g-list-cell__content">
                                <span
                                    data-terms-and-conditions-checkbox
                                    className="m2g-icon m2g-icon--checkbox m2g-icon--tick"
                                />
                                <span>{t('customer:i_agree_to')}</span>
                                <span
                                    role="button"
                                    tabIndex={0}
                                    data-terms-and-conditions-link
                                    className="m2g-customer-terms-and-conditions-link m2g-text--link"
                                    onClick={e => {
                                        e.stopPropagation();
                                        Mobi2Go.UI.trigger(
                                            'showPolicy',
                                            'termsandconditions'
                                        );
                                    }}
                                    onKeyUp={e => {
                                        if (e.key !== ' ') {
                                            return;
                                        }
                                        e.stopPropagation();
                                        Mobi2Go.UI.trigger(
                                            'showPolicy',
                                            'termsandconditions'
                                        );
                                    }}
                                >
                                    {t('customer:terms_and_conditions')}
                                </span>
                            </div>
                        </div>

                        <div
                            role="checkbox"
                            tabIndex={0}
                            data-subscriber
                            data-checked={subscriber}
                            aria-checked={subscriber}
                            className="m2g-list-cell m2g-customer-subscriber m2g-touchable"
                            onClick={() => setSubscriber(!subscriber)}
                            onKeyUp={e => {
                                if (e.key !== ' ') {
                                    return;
                                }
                                setSubscriber(!subscriber);
                            }}
                        >
                            <div className="m2g-list-cell__content">
                                <span
                                    data-subscriber-checkbox
                                    className="m2g-customer-subscriber-checkbox m2g-icon m2g-icon--checkbox m2g-icon--tick"
                                />
                                <label
                                    data-subscriber-label
                                    className="m2g-customer-subscriber-label"
                                >
                                    {t('customer:subscriber_question')}
                                </label>
                            </div>
                        </div>
                    </>
                )}

                <button
                    type="button"
                    id="m2g-customer-phone-number-submit-button"
                    className="m2g-customer-phone-number-submit-button m2g-button m2g-button--primary m2g-button--fat"
                    tabIndex="0"
                    onKeyDown={() => {}}
                    onClick={saveToServer}
                    disabled={!termsAndConditions || isLoading}
                >
                    {isLoading ? (
                        <DotLoader dotCharacter="•" />
                    ) : (
                        t('customer.verification:submit-button-label')
                    )}
                </button>
            </>
        </div>
    );
});

CustomerPhoneNumberForm.propTypes = {
    onComplete: PropTypes.func.isRequired,
};

CustomerPhoneNumberForm.displayName = 'CustomerPhoneNumberForm';

export default CustomerPhoneNumberForm;
